// ---------------------------------
// client/src/pages/AdminUsers.jsx
// Admin view for managing user approvals and activation state
// ---------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import jwtDecode from 'jwt-decode';
import api from '../utils/api';
import {
    fetchAdminRecipients,
    createRecipient,
    updateRecipient,
    deleteRecipient,
    setDefaultFinishedJobRecipient,
} from '../utils/recipients';
import { formatDate } from '../utils/date';

const TEXT = {
    title: 'ניהול משתמשים',
    pendingTitle: 'בקשות הצטרפות',
    activeOverviewTitle: 'משתמשים מאושרים',
    loading: 'טוען...',
    loadError: 'אירעה שגיאה בטעינת המשתמשים.',
    noPending: 'אין בקשות הצטרפות ממתינות.',
    noApprovedUsers: 'אין משתמשים מאושרים במערכת.',
    nameHeader: 'שם',
    emailHeader: 'אימייל',
    roleHeader: 'תפקיד',
    createdHeader: 'נוצר בתאריך',
    lastSeenHeader: 'פעילות אחרונה',
    loginHeader: 'כניסה',
    ipHeader: 'IP אחרון',
    deviceHeader: 'דפדפן / מכשיר',
    statusHeader: 'סטטוס',
    actionsHeader: 'פעולות',
    statusPending: 'ממתין לאישור',
    statusActive: 'פעיל',
    statusInactive: 'לא פעיל',
    approve: 'אשר',
    decline: 'דחה',
    approveError: 'לא ניתן לאשר משתמש.',
    declineError: 'לא ניתן לדחות משתמש.',
    activate: 'הפעל משתמש',
    deactivate: 'השבת משתמש',
    loggedIn: 'מחובר',
    loggedOut: 'לא מחובר',
    sessions: 'סשנים',
    forceLogout: 'נתק בכפייה',
    forceLoggingOut: 'מנתק...',
    forceLogoutConfirm: 'לנתק את המשתמש מכל המכשירים?',
    forceLogoutError: 'לא ניתן לנתק את המשתמש.',
    deletePermanent: 'מחק לצמיתות',
    deleting: 'מוחק...',
    deleteConfirm: 'למחוק את המשתמש לצמיתות? פעולה זו אפשרית רק אם אין לו היסטוריית עבודות.',
    deleteError: 'לא ניתן למחוק את המשתמש.',
    currentUserLabel: 'החשבון שלך',
    approving: 'מאשר...',
    declining: 'דוחה...',
    activating: 'מפעיל...',
    deactivating: 'משבית...',
    updateError: 'לא ניתן לעדכן משתמש.',
    recipientsTitle: 'רשימת נמענים',
    recipientsDescription: 'ניהול רשימת הנמענים לשליחת מיילים.',
    recipientName: 'שם',
    recipientEmail: 'אימייל',
    recipientAdd: 'הוסף נמען',
    recipientSave: 'שמור',
    recipientCancel: 'ביטול',
    recipientEdit: 'עריכה',
    recipientDelete: 'מחיקה',
    recipientDefault: 'ברירת מחדל לסיום עבודה',
    recipientSetDefault: 'קבע כברירת מחדל',
    recipientDeleteConfirm: 'למחוק את הנמען?',
    recipientNoData: 'אין נמענים ברשימה.',
    recipientLoadError: 'שגיאה בטעינת רשימת הנמענים.',
    recipientAddError: 'לא ניתן להוסיף נמען.',
    recipientUpdateError: 'לא ניתן לעדכן נמען.',
    recipientDeleteError: 'לא ניתן למחוק נמען.',
    recipientDefaultError: 'לא ניתן לקבוע נמען ברירת מחדל.',
};

function resolveCurrentUserId(token) {
    if (!token) {
        return null;
    }
    try {
        const payload = jwtDecode(token);
        return payload?.id ?? null;
    } catch (err) {
        return null;
    }
}

function formatDateTime(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${formatDate(value)} ${hours}:${minutes}`;
}

function summarizeUserAgent(value) {
    const text = String(value || '').trim();
    if (!text) return '-';
    return text.length > 90 ? `${text.slice(0, 90)}...` : text;
}

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionError, setActionError] = useState('');
    const [updating, setUpdating] = useState(() => new Set());
    const [recipients, setRecipients] = useState([]);
    const [recipientsLoading, setRecipientsLoading] = useState(true);
    const [recipientsError, setRecipientsError] = useState('');
    const [recipientActionError, setRecipientActionError] = useState('');
    const [newRecipientName, setNewRecipientName] = useState('');
    const [newRecipientEmail, setNewRecipientEmail] = useState('');
    const [editingRecipientId, setEditingRecipientId] = useState('');
    const [editingRecipientName, setEditingRecipientName] = useState('');
    const [editingRecipientEmail, setEditingRecipientEmail] = useState('');
    const [recipientUpdating, setRecipientUpdating] = useState(() => new Set());

    const currentUserId = resolveCurrentUserId(localStorage.getItem('token'));

    useEffect(() => {
        const loadUsers = async () => {
            try {
                setLoading(true);
                setError('');
                const { data } = await api.get('/admin/users');
                setUsers(data);
            } catch (err) {
                setError(err.response?.data?.message || TEXT.loadError);
            } finally {
                setLoading(false);
            }
        };

        loadUsers();
    }, []);

    useEffect(() => {
        const loadRecipients = async () => {
            try {
                setRecipientsLoading(true);
                setRecipientsError('');
                const data = await fetchAdminRecipients();
                setRecipients(Array.isArray(data) ? data : []);
            } catch (err) {
                setRecipientsError(err.response?.data?.message || TEXT.recipientLoadError);
                setRecipients([]);
            } finally {
                setRecipientsLoading(false);
            }
        };

        loadRecipients();
    }, []);

    const pendingUsers = useMemo(() => users.filter((user) => !user.approved), [users]);
    const approvedUsers = useMemo(() => users.filter((user) => user.approved), [users]);

    const setUpdatingState = (userId, isActive) => {
        setUpdating((prev) => {
            const next = new Set(prev);
            if (isActive) {
                next.add(userId);
            } else {
                next.delete(userId);
            }
            return next;
        });
    };

    const approveUser = async (userId) => {
        try {
            setUpdatingState(userId, true);
            setActionError('');
            await api.patch(`/admin/users/${userId}/approve`);
            setUsers((prev) =>
                prev.map((user) =>
                    user.id === userId
                        ? { ...user, approved: true, isActive: true }
                        : user,
                ),
            );
        } catch (err) {
            setActionError(err.response?.data?.message || TEXT.approveError);
        } finally {
            setUpdatingState(userId, false);
        }
    };

    const toggleUserActive = async (userId, nextState) => {
        try {
            setUpdatingState(userId, true);
            setActionError('');
            const { data } = await api.patch(`/admin/users/${userId}/active`, { isActive: nextState });
            setUsers((prev) =>
                prev.map((user) => (
                    user.id === userId
                        ? {
                              ...user,
                              isActive: data?.isActive ?? nextState,
                              isLoggedIn: data?.isLoggedIn ?? (nextState ? user.isLoggedIn : false),
                              activeSessionCount: data?.activeSessionCount ?? (nextState ? user.activeSessionCount : 0),
                          }
                        : user
                )),
            );
        } catch (err) {
            setActionError(err.response?.data?.message || TEXT.updateError);
        } finally {
            setUpdatingState(userId, false);
        }
    };

    const forceLogoutUser = async (userId) => {
        if (!userId || !window.confirm(TEXT.forceLogoutConfirm)) return;
        try {
            setUpdatingState(userId, true);
            setActionError('');
            const { data } = await api.post(`/admin/users/${userId}/force-logout`);
            setUsers((prev) =>
                prev.map((user) => (
                    user.id === userId
                        ? {
                              ...user,
                              isLoggedIn: data?.isLoggedIn ?? false,
                              activeSessionCount: data?.activeSessionCount ?? 0,
                              activeSessions: [],
                          }
                        : user
                )),
            );
        } catch (err) {
            setActionError(err.response?.data?.message || TEXT.forceLogoutError);
        } finally {
            setUpdatingState(userId, false);
        }
    };

    const deleteUserPermanent = async (userId) => {
        if (!userId || !window.confirm(TEXT.deleteConfirm)) return;
        try {
            setUpdatingState(userId, true);
            setActionError('');
            await api.delete(`/admin/users/${userId}`);
            setUsers((prev) => prev.filter((user) => user.id !== userId));
        } catch (err) {
            setActionError(err.response?.data?.message || TEXT.deleteError);
        } finally {
            setUpdatingState(userId, false);
        }
    };

    const declineUser = async (userId) => {
        try {
            setUpdatingState(userId, true);
            setActionError('');
            await api.post(`/admin/users/${userId}/decline`);
            setUsers((prev) => prev.filter((user) => user.id !== userId));
        } catch (err) {
            setActionError(err.response?.data?.message || TEXT.declineError);
        } finally {
            setUpdatingState(userId, false);
        }
    };

    const setRecipientUpdatingState = (recipientId, isActive) => {
        setRecipientUpdating((prev) => {
            const next = new Set(prev);
            if (isActive) {
                next.add(recipientId);
            } else {
                next.delete(recipientId);
            }
            return next;
        });
    };

    const handleAddRecipient = async (event) => {
        event.preventDefault();
        if (!newRecipientName.trim() || !newRecipientEmail.trim()) {
            setRecipientActionError(TEXT.recipientAddError);
            return;
        }
        try {
            setRecipientActionError('');
            setRecipientUpdatingState('new', true);
            const created = await createRecipient({
                name: newRecipientName.trim(),
                email: newRecipientEmail.trim(),
            });
            setRecipients((prev) => [created, ...prev]);
            setNewRecipientName('');
            setNewRecipientEmail('');
        } catch (err) {
            setRecipientActionError(err.response?.data?.message || TEXT.recipientAddError);
        } finally {
            setRecipientUpdatingState('new', false);
        }
    };

    const startEditRecipient = (recipient) => {
        setEditingRecipientId(recipient.id);
        setEditingRecipientName(recipient.name || '');
        setEditingRecipientEmail(recipient.email || '');
        setRecipientActionError('');
    };

    const cancelEditRecipient = () => {
        setEditingRecipientId('');
        setEditingRecipientName('');
        setEditingRecipientEmail('');
    };

    const handleSaveRecipient = async (recipientId) => {
        try {
            setRecipientActionError('');
            setRecipientUpdatingState(recipientId, true);
            const updated = await updateRecipient(recipientId, {
                name: editingRecipientName.trim(),
                email: editingRecipientEmail.trim(),
            });
            setRecipients((prev) =>
                prev.map((recipient) => (recipient.id === recipientId ? updated : recipient)),
            );
            cancelEditRecipient();
        } catch (err) {
            setRecipientActionError(err.response?.data?.message || TEXT.recipientUpdateError);
        } finally {
            setRecipientUpdatingState(recipientId, false);
        }
    };

    const handleDeleteRecipient = async (recipientId) => {
        if (!recipientId) return;
        if (!window.confirm(TEXT.recipientDeleteConfirm)) return;
        try {
            setRecipientActionError('');
            setRecipientUpdatingState(recipientId, true);
            await deleteRecipient(recipientId);
            setRecipients((prev) => prev.filter((recipient) => recipient.id !== recipientId));
        } catch (err) {
            setRecipientActionError(err.response?.data?.message || TEXT.recipientDeleteError);
        } finally {
            setRecipientUpdatingState(recipientId, false);
        }
    };

    const handleSetDefaultRecipient = async (recipientId) => {
        if (!recipientId) return;
        try {
            setRecipientActionError('');
            setRecipientUpdatingState(recipientId, true);
            const updated = await setDefaultFinishedJobRecipient(recipientId);
            setRecipients((prev) =>
                prev
                    .map((recipient) => ({
                        ...recipient,
                        isDefaultForFinishedJobs: recipient.id === updated.id,
                    }))
                    .sort((a, b) => {
                        const defaultRank =
                            Number(Boolean(b.isDefaultForFinishedJobs)) -
                            Number(Boolean(a.isDefaultForFinishedJobs));
                        if (defaultRank !== 0) return defaultRank;
                        return String(a.name || '').localeCompare(String(b.name || ''), 'he');
                    }),
            );
        } catch (err) {
            setRecipientActionError(err.response?.data?.message || TEXT.recipientDefaultError);
        } finally {
            setRecipientUpdatingState(recipientId, false);
        }
    };

    if (loading) {
        return (
            <div className="page page--admin-users page--accent">
                <div className="card">{TEXT.loading}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page page--admin-users page--accent">
                <div className="alert alert--danger">{error}</div>
            </div>
        );
    }

    return (
        <div className="page page--admin-users page--accent stack stack--lg">
            <header className="page__header">
                <h1 className="page__title">{TEXT.title}</h1>
            </header>

            {actionError && <div className="alert alert--danger">{actionError}</div>}

            <section className="card stack">
                <h2 className="card__title">{TEXT.pendingTitle}</h2>
                {pendingUsers.length === 0 ? (
                    <p className="card__meta">{TEXT.noPending}</p>
                ) : (
                    <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>{TEXT.nameHeader}</th>
                                <th>{TEXT.emailHeader}</th>
                                <th>{TEXT.createdHeader}</th>
                                <th>{TEXT.actionsHeader}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingUsers.map((user) => (
                                <tr key={user.id}>
                                    <td>{user.name}</td>
                                    <td>{user.email}</td>
                                    <td>{formatDate(user.createdAt)}</td>
                                    <td>
                                        <div className="flex flex--wrap">
                                            <button
                                                type="button"
                                                className="button button--primary"
                                                onClick={() => approveUser(user.id)}
                                                disabled={updating.has(user.id)}
                                            >
                                                {updating.has(user.id) ? TEXT.approving : TEXT.approve}
                                            </button>
                                            <button
                                                type="button"
                                                className="button button--danger"
                                                onClick={() => declineUser(user.id)}
                                                disabled={updating.has(user.id)}
                                            >
                                                {updating.has(user.id) ? TEXT.declining : TEXT.decline}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </section>

            <section className="card stack">
                <h2 className="card__title">{TEXT.activeOverviewTitle}</h2>
                {approvedUsers.length === 0 ? (
                    <p className="card__meta">{TEXT.noApprovedUsers}</p>
                ) : (
                    <div className="admin-users-list">
                        {approvedUsers.map((user) => {
                            const isCurrent = user.id === currentUserId;
                            const isUpdating = updating.has(user.id);
                            const nextState = !user.isActive;
                            const sessionCount = Number(user.activeSessionCount) || 0;
                            const buttonLabel = isUpdating
                                ? user.isActive
                                    ? TEXT.deactivating
                                    : TEXT.activating
                                : user.isActive
                                    ? TEXT.deactivate
                                    : TEXT.activate;
                            return (
                                <article
                                    key={user.id}
                                    className={[
                                        'admin-user-card',
                                        user.isActive ? '' : 'admin-user-card--inactive',
                                    ].filter(Boolean).join(' ')}
                                >
                                    <div className="admin-user-card__header">
                                        <div className="admin-user-card__identity">
                                            <h3 className="admin-user-card__name">{user.name}</h3>
                                            <span className="admin-user-card__email">{user.email}</span>
                                        </div>
                                        <div className="admin-user-card__badges">
                                            {isCurrent && <span className="chip chip--info">{TEXT.currentUserLabel}</span>}
                                            <span className="chip">{user.role || '-'}</span>
                                            <span className={`chip ${user.isActive ? 'chip--success' : 'chip--danger'}`}>
                                                {user.isActive ? TEXT.statusActive : TEXT.statusInactive}
                                            </span>
                                            <span className={`chip ${user.isLoggedIn ? 'chip--success' : 'chip--info'}`}>
                                                {user.isLoggedIn ? TEXT.loggedIn : TEXT.loggedOut}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="admin-user-card__meta">
                                        <div className="admin-user-card__meta-item">
                                            <span className="admin-user-card__label">{TEXT.createdHeader}</span>
                                            <span className="admin-user-card__value">{formatDate(user.createdAt)}</span>
                                        </div>
                                        <div className="admin-user-card__meta-item">
                                            <span className="admin-user-card__label">{TEXT.lastSeenHeader}</span>
                                            <span className="admin-user-card__value">{formatDateTime(user.lastSeenAt)}</span>
                                        </div>
                                        <div className="admin-user-card__meta-item">
                                            <span className="admin-user-card__label">{TEXT.sessions}</span>
                                            <span className="admin-user-card__value">{sessionCount}</span>
                                        </div>
                                        <div className="admin-user-card__meta-item">
                                            <span className="admin-user-card__label">{TEXT.ipHeader}</span>
                                            <span className="admin-user-card__value admin-user-card__value--mono">
                                                {user.lastIp || '-'}
                                            </span>
                                        </div>
                                        <div className="admin-user-card__meta-item admin-user-card__meta-item--wide">
                                            <span className="admin-user-card__label">{TEXT.deviceHeader}</span>
                                            <span className="admin-user-card__value admin-user-card__device" title={user.lastUserAgent || ''}>
                                                {summarizeUserAgent(user.lastUserAgent)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="admin-user-card__actions">
                                        <button
                                            type="button"
                                            className={user.isActive ? 'button button--danger' : 'button button--primary'}
                                            onClick={() => toggleUserActive(user.id, nextState)}
                                            disabled={isUpdating || isCurrent}
                                        >
                                            {buttonLabel}
                                        </button>
                                        <button
                                            type="button"
                                            className="button button--light"
                                            onClick={() => forceLogoutUser(user.id)}
                                            disabled={isUpdating || isCurrent || !user.isLoggedIn}
                                        >
                                            {isUpdating && user.isLoggedIn ? TEXT.forceLoggingOut : TEXT.forceLogout}
                                        </button>
                                        <button
                                            type="button"
                                            className="button button--danger"
                                            onClick={() => deleteUserPermanent(user.id)}
                                            disabled={isUpdating || isCurrent}
                                        >
                                            {isUpdating ? TEXT.deleting : TEXT.deletePermanent}
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </section>

            <section className="card stack">
                <h2 className="card__title">{TEXT.recipientsTitle}</h2>
                <p className="card__meta">{TEXT.recipientsDescription}</p>

                <form className="stack" onSubmit={handleAddRecipient}>
                    <div className="flex flex--wrap">
                        <label className="form-field">
                            <span className="form-field__label">{TEXT.recipientName}</span>
                            <input
                                className="form-field__control"
                                type="text"
                                value={newRecipientName}
                                onChange={(event) => setNewRecipientName(event.target.value)}
                                required
                            />
                        </label>
                        <label className="form-field">
                            <span className="form-field__label">{TEXT.recipientEmail}</span>
                            <input
                                className="form-field__control"
                                type="email"
                                value={newRecipientEmail}
                                onChange={(event) => setNewRecipientEmail(event.target.value)}
                                required
                            />
                        </label>
                        <button
                            type="submit"
                            className="button button--primary"
                            disabled={recipientUpdating.has('new')}
                        >
                            {recipientUpdating.has('new') ? TEXT.loading : TEXT.recipientAdd}
                        </button>
                    </div>
                </form>

                {recipientActionError && <div className="alert alert--danger">{recipientActionError}</div>}

                {recipientsLoading ? (
                    <div className="card__meta">{TEXT.loading}</div>
                ) : recipients.length === 0 ? (
                    <div className="card__meta">{recipientsError || TEXT.recipientNoData}</div>
                ) : (
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>{TEXT.recipientName}</th>
                                <th>{TEXT.recipientEmail}</th>
                                <th>{TEXT.actionsHeader}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recipients.map((recipient) => {
                                const isEditing = editingRecipientId === recipient.id;
                                const isUpdatingRecipient = recipientUpdating.has(recipient.id);
                                return (
                                    <tr key={recipient.id}>
                                        <td>
                                            {isEditing ? (
                                                <input
                                                    className="form-field__control"
                                                    type="text"
                                                    value={editingRecipientName}
                                                    onChange={(event) => setEditingRecipientName(event.target.value)}
                                                />
                                            ) : (
                                                recipient.name
                                            )}
                                            {!isEditing && recipient.isDefaultForFinishedJobs && (
                                                <span className="chip chip--success">{TEXT.recipientDefault}</span>
                                            )}
                                        </td>
                                        <td>
                                            {isEditing ? (
                                                <input
                                                    className="form-field__control"
                                                    type="email"
                                                    value={editingRecipientEmail}
                                                    onChange={(event) => setEditingRecipientEmail(event.target.value)}
                                                />
                                            ) : (
                                                recipient.email
                                            )}
                                        </td>
                                        <td>
                                            {isEditing ? (
                                                <div className="flex flex--wrap">
                                                    <button
                                                        type="button"
                                                        className="button button--primary"
                                                        onClick={() => handleSaveRecipient(recipient.id)}
                                                        disabled={isUpdatingRecipient}
                                                    >
                                                        {isUpdatingRecipient ? TEXT.loading : TEXT.recipientSave}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="button button--ghost"
                                                        onClick={cancelEditRecipient}
                                                        disabled={isUpdatingRecipient}
                                                    >
                                                        {TEXT.recipientCancel}
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex flex--wrap">
                                                    <button
                                                        type="button"
                                                        className="button button--primary"
                                                        onClick={() => handleSetDefaultRecipient(recipient.id)}
                                                        disabled={isUpdatingRecipient || recipient.isDefaultForFinishedJobs}
                                                    >
                                                        {recipient.isDefaultForFinishedJobs
                                                            ? TEXT.recipientDefault
                                                            : TEXT.recipientSetDefault}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="button button--light"
                                                        onClick={() => startEditRecipient(recipient)}
                                                    >
                                                        {TEXT.recipientEdit}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="button button--danger"
                                                        onClick={() => handleDeleteRecipient(recipient.id)}
                                                        disabled={isUpdatingRecipient}
                                                    >
                                                        {TEXT.recipientDelete}
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}
